// Language picker for a code block, opened from the block's language
// chip. Portals a searchable list anchored at the chip rect; selecting a
// language commits via the editor's `setCodeBlockLanguage`. Web-oriented
// (the chip + rect are DOM-driven) but built from RN components so it
// themes with the rest of the composer.

import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";
import { Check } from "lucide-react-native";
import { CODE_LANGUAGES, languageLabel } from "@ping/enriched-text-input";
import { useColorScheme } from "../../foundations/colorScheme";
import { tokens } from "../../design-system/tokens";
import { WebPortal } from "./WebPortal";

export interface CodeLanguagePickerOverlayProps {
  /** Viewport rect of the chip the picker anchors to. */
  readonly anchorRect: { x: number; y: number; width: number; height: number } | null;
  /** Current language id ('' / 'plaintext' = Plain Text). */
  readonly currentLanguage: string;
  readonly onSelect: (id: string) => void;
  readonly onClose: () => void;
  readonly testID?: string;
}

const PANEL_WIDTH = 220;
const PANEL_MAX_HEIGHT = 300;
const GAP = 6;

export function CodeLanguagePickerOverlay({
  anchorRect,
  currentLanguage,
  onSelect,
  onClose,
  testID,
}: CodeLanguagePickerOverlayProps): React.ReactElement | null {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const colors = tokens.color.semantic[colorScheme];
  const [query, setQuery] = useState("");

  const activeId =
    currentLanguage.length === 0 ? "plaintext" : currentLanguage.toLowerCase();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return CODE_LANGUAGES;
    return CODE_LANGUAGES.filter(
      (l) => l.label.toLowerCase().includes(q) || l.id.toLowerCase().includes(q),
    );
  }, [query]);

  if (anchorRect === null) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  // Below the chip by default; flip above when there's no room.
  let top = anchorRect.y + anchorRect.height + GAP;
  if (top + PANEL_MAX_HEIGHT > vh - 12) {
    top = Math.max(12, anchorRect.y - GAP - PANEL_MAX_HEIGHT);
  }
  // Right-align to the chip (chips sit at the block's right edge).
  let left = anchorRect.x + anchorRect.width - PANEL_WIDTH;
  if (left + PANEL_WIDTH > vw - 12) left = vw - PANEL_WIDTH - 12;
  if (left < 12) left = 12;

  return (
    <WebPortal>
      {/* Click-outside backdrop. */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "transparent",
          zIndex: 10000,
        }}
      />
      <div
        data-testid={testID}
        style={{
          position: "fixed",
          top,
          left,
          width: PANEL_WIDTH,
          maxHeight: PANEL_MAX_HEIGHT,
          zIndex: 10001,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow:
            "0 10px 28px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.16)",
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <View className="rounded-[12px] bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]">
          <View className="px-2 pt-2 pb-1">
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search language…"
              placeholderTextColor={colors.text.secondary}
              className="h-8 px-2 rounded-lg bg-light-surface-secondary dark:bg-[#222429] font-sans text-[13px] text-light-text-primary dark:text-white"
              autoFocus
              testID="code-language-search"
            />
          </View>
          <ScrollView
            style={{ maxHeight: PANEL_MAX_HEIGHT - 52 }}
            keyboardShouldPersistTaps="handled"
          >
            {filtered.map((lang) => {
              const selected = lang.id === activeId;
              return (
                <Pressable
                  key={lang.id}
                  onPress={() => onSelect(lang.id)}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected }}
                  className={`
                    flex flex-row items-center justify-between px-3 py-[7px]
                    ${selected ? "bg-light-surface-secondary dark:bg-white/[0.06]" : ""}
                  `}
                  testID={`code-language-${lang.id}`}
                >
                  <Text className="font-sans text-[14px] text-light-text-primary dark:text-[#F5F6F8]">
                    {languageLabel(lang.id)}
                  </Text>
                  {selected ? (
                    <Check size={14} strokeWidth={2} color={colors.accent.indigo} />
                  ) : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </div>
    </WebPortal>
  );
}
